import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Chip,
  FileIndicatorsView,
  HelpTip,
  ProcessingProgress,
  StatusChip,
  cn,
  documentStatusKey,
  errorMessage,
  categoryPathLabel,
  fileIndicatorsOf,
  formatDateTime,
  noteGuideActivity,
  type RoomDocument,
} from '@dataroom/shared';
import { useProcessingEstimate, useRoomDocuments, useRoomProcessingQueue } from '@/api/hooks';
import { DocumentPreviewModal } from '@/components/DocumentPreviewModal';
import { VersionSuggestionsReview } from '@/components/VersionSuggestionsReview';
import {
  attentionOf,
  filingReviewPath,
  processingHeadline,
  processingSummary,
  redactionReviewPath,
  type Attention,
  type ProcessingQueueFilter,
} from '@/lib/processing-queue';
import { useRoomContext } from './context';

/** The page's state in the URL: `?filter=attention`, `?page=2`. */
function stateFrom(search: URLSearchParams): { filter: ProcessingQueueFilter; page: number } {
  const page = Number(search.get('page'));
  return {
    filter: search.get('filter') === 'attention' ? 'needs_attention' : 'unfinished',
    page: Number.isInteger(page) && page > 1 ? page : 1,
  };
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * The room's PROCESSING page (owner, 2026-10-15): ONLY documents that have not
 * finished every stage — still classifying / OCR / redacting, or waiting on a
 * person (marked in-row, with the action that unblocks it). A document leaves
 * the list, live, the moment its last stage finishes — automatically or after a
 * person acted. Everything here is server state (polled while a stage runs), so
 * it survives leaving the upload panel, a reload, or another device.
 */
export function RoomProcessingPage() {
  const { roomId } = useRoomContext();
  // The free-trial guide's "Watch it work" ticks once this page has been opened.
  useEffect(() => noteGuideActivity('processing'), []);
  const [search, setSearch] = useSearchParams();
  const { filter, page } = stateFrom(search);
  const view = useRoomProcessingQueue(roomId, filter, page);
  const [preview, setPreview] = useState<RoomDocument | null>(null);
  // A document named by id (from the "newer versions" bar): found in the room's listing.
  const [previewById, setPreviewById] = useState<string | null>(null);
  const previewId = preview?.id ?? previewById;
  // The viewer reads the document LIVE: from this page while it is unfinished,
  // then from the room's listing once its last stage finishes and it leaves this
  // list — never the snapshot taken when it was opened, which would keep saying
  // "Being prepared" (or the old filing) for a document that is done (review F1).
  // Read only while the viewer is open.
  const roomDocuments = useRoomDocuments(roomId, { enabled: previewId !== null });
  const previewInView =
    previewId !== null && (view.data?.documents.some((d) => d.id === previewId) ?? false);
  const refetchRoomDocuments = roomDocuments.refetch;
  useEffect(() => {
    // It just left this list: re-read the listing so the viewer shows where it ended.
    if (previewId !== null && view.data && !previewInView) void refetchRoomDocuments();
  }, [previewId, previewInView, view.data, refetchRoomDocuments]);
  const previewDoc =
    previewId === null
      ? null
      : (view.data?.documents.find((d) => d.id === previewId) ??
        roomDocuments.data?.find((d) => d.id === previewId) ??
        preview);
  const navigate = useNavigate();
  const reviewRedaction = (d: RoomDocument) => navigate(redactionReviewPath(roomId, d.id));
  // The redaction review walk ("Next document") comes back here when none is left.
  const location = useLocation();
  const finishedRedactionWalk =
    (location.state as { redactionsReviewed?: boolean } | null)?.redactionsReviewed === true;

  const go = (next: { filter: ProcessingQueueFilter; page: number }) => {
    const s = new URLSearchParams();
    if (next.filter === 'needs_attention') s.set('filter', 'attention');
    if (next.page > 1) s.set('page', String(next.page));
    setSearch(s, { replace: true });
  };

  const data = view.data;
  const summary = data ? processingSummary(data.counts) : null;
  const settled = summary ? summary.all - summary.unfinished : 0;
  const percent = summary && summary.all > 0 ? Math.round((settled / summary.all) * 100) : 0;

  // The estimate polls itself every ~10 s while work remains; when it last read
  // "nothing left" but the list shows a stage running (new documents arrived while
  // the page was open), read it again.
  const estimate = useProcessingEstimate(roomId);
  const refetchEstimate = estimate.refetch;
  const estimateLeft = estimate.data?.documentsLeft;
  const runningNow = summary?.inProgress ?? 0;
  useEffect(() => {
    if (estimateLeft === 0 && runningNow > 0) void refetchEstimate();
  }, [estimateLeft, runningNow, refetchEstimate]);

  return (
    <div className="space-y-6">
      {/* The same "uploads that look like newer versions" bar as the Data room. */}
      <VersionSuggestionsReview
        roomId={roomId}
        onOpenDocument={(docId) => {
          setPreview(null);
          setPreviewById(docId);
        }}
      />
      <Card>
        <CardHeader>
          <CardTitle>Processing</CardTitle>
          <p className="text-sm text-muted-foreground">
            Documents still being filed, read (OCR) or redacted — and any waiting on you. A document
            leaves this list as soon as every step is finished.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {finishedRedactionWalk ? (
            <p
              role="status"
              className="rounded-lg border border-st-filed-line bg-st-filed p-3 text-sm font-medium text-st-filed-fg"
            >
              All redactions reviewed.
            </p>
          ) : null}
          {/* The running tally by stage and the time left (owner ask 2026-10-23). */}
          <ProcessingProgress
            estimate={estimate.data}
            waitingOnPeople={summary?.needsAttention ?? 0}
          />
          {view.error ? (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-destructive" role="alert">
                {errorMessage(view.error) ?? 'Could not load the documents.'}
              </p>
              <Button type="button" variant="outline" size="sm" onClick={() => void view.refetch()}>
                Try again
              </Button>
            </div>
          ) : !data || !summary ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : summary.unfinished === 0 ? (
            <div
              className="space-y-2 rounded-lg border border-dashed p-4"
              data-testid="processing-empty"
            >
              <p className="font-medium">Nothing is processing — all documents are finished.</p>
              <p className="text-sm text-ink-2">
                {summary.finished === 0
                  ? 'No documents have been added to this room yet.'
                  : `${plural(summary.finished, 'document', 'documents')} finished.`}
              </p>
              <Link
                to={`/rooms/${roomId}`}
                className="inline-block text-sm font-medium text-primary hover:underline"
              >
                Back to the data room
              </Link>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <p className="text-sm font-medium tabular-nums" data-testid="processing-headline">
                  {processingHeadline(summary)}
                </p>
                <div
                  className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
                  role="progressbar"
                  aria-label={`${settled} of ${summary.all} documents finished`}
                  aria-valuenow={percent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className="h-full rounded-full bg-primary transition-[width] duration-500"
                    style={{ width: `${percent}%` }}
                  />
                </div>
              </div>

              <div role="group" aria-label="Show" className="flex flex-wrap items-center gap-2">
                <FilterChip
                  active={filter === 'unfinished'}
                  label="All in progress"
                  count={summary.unfinished}
                  onClick={() => go({ filter: 'unfinished', page: 1 })}
                />
                <FilterChip
                  active={filter === 'needs_attention'}
                  label="Needs your attention"
                  count={summary.needsAttention}
                  onClick={() => go({ filter: 'needs_attention', page: 1 })}
                />
                <HelpTip id="admin.processing.needs-attention" className="-ml-1" />
              </div>

              {data.documents.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing needs your attention.</p>
              ) : (
                <ul aria-label="Documents in progress" className="divide-y rounded-lg border">
                  {data.documents.map((d) => (
                    <ProcessingRow
                      key={d.id}
                      doc={d}
                      roomId={roomId}
                      onView={() => {
                        setPreviewById(null);
                        setPreview(d);
                      }}
                    />
                  ))}
                </ul>
              )}

              {data.pageCount > 1 ? (
                <nav
                  aria-label="Pages"
                  className="flex flex-wrap items-center justify-between gap-2 text-sm"
                >
                  <span className="text-ink-2">
                    {plural(data.total, 'document', 'documents')} · page {data.page} of{' '}
                    {data.pageCount}
                  </span>
                  <span className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={data.page <= 1}
                      onClick={() => go({ filter, page: data.page - 1 })}
                    >
                      Previous
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={data.page >= data.pageCount}
                      onClick={() => go({ filter, page: data.page + 1 })}
                    >
                      Next
                    </Button>
                  </span>
                </nav>
              ) : null}
            </>
          )}
        </CardContent>
        {previewDoc ? (
          <DocumentPreviewModal
            roomId={roomId}
            // The live copy (the polled view, else the room's listing), so a change
            // made in "Manage document" or a finished stage shows once it re-reads.
            document={previewDoc}
            onClose={() => {
              setPreview(null);
              setPreviewById(null);
            }}
            onReviewRedaction={reviewRedaction}
            canManage
          />
        ) : null}
      </Card>
    </div>
  );
}

function FilterChip({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex h-9 items-center gap-1.5 rounded-pill border px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-input bg-background hover:bg-surface-2',
      )}
    >
      {label}
      <span className={cn('tabular-nums', active ? 'opacity-90' : 'text-ink-3')}>({count})</span>
    </button>
  );
}

/** The action that unblocks a document waiting on a person. */
function AttentionAction({
  attention,
  doc,
  roomId,
  onView,
}: {
  attention: Attention;
  doc: RoomDocument;
  roomId: string;
  onView: () => void;
}) {
  const linkClass =
    'inline-flex h-9 items-center rounded-md px-3 text-sm font-medium text-primary hover:bg-surface-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
  if (attention.kind === 'filing') {
    return (
      <Link to={filingReviewPath(roomId, doc.id)} className={linkClass}>
        {attention.action}
        <span className="sr-only"> of {doc.name}</span>
      </Link>
    );
  }
  if (attention.kind === 'redaction') {
    return (
      <Link to={redactionReviewPath(roomId, doc.id)} className={linkClass}>
        {attention.action}
        <span className="sr-only"> of {doc.name}</span>
      </Link>
    );
  }
  return doc.downloadReady ? (
    <Button type="button" variant="ghost" size="sm" onClick={onView}>
      {attention.action}
      <span className="sr-only"> {doc.name}</span>
    </Button>
  ) : null;
}

function ProcessingRow({
  doc: d,
  roomId,
  onView,
}: {
  doc: RoomDocument;
  roomId: string;
  onView: () => void;
}) {
  const attention = attentionOf(d);
  return (
    <li className="grid grid-cols-1 gap-2 p-3 text-sm md:grid-cols-[minmax(0,1fr)_240px_180px_auto] md:items-start md:gap-4">
      <div className="min-w-0">
        <span className="block truncate font-medium">{d.name}</span>
        {d.categoryPath.length > 0 ? (
          <span className="block truncate text-xs text-ink-3">
            {categoryPathLabel(d.categoryPath)}
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-start gap-1">
        <StatusChip
          status={documentStatusKey(
            d.classificationStatus,
            d.status,
            d.paperworkKind,
            Boolean(d.archive),
            d.processingStatus,
            d.categoryPath.length > 0,
          )}
        />
        <FileIndicatorsView indicators={fileIndicatorsOf(d)} />
        {attention ? (
          <Chip tone="check" icon="alert">
            {attention.label}
          </Chip>
        ) : null}
      </div>
      <div className="min-w-0 text-xs text-ink-2">
        <span className="block truncate">{d.uploadedBy ?? '—'}</span>
        <span className="block tabular-nums text-ink-3">{formatDateTime(d.createdAt)}</span>
      </div>
      <div className="md:text-right">
        {attention ? (
          <AttentionAction attention={attention} doc={d} roomId={roomId} onView={onView} />
        ) : d.downloadReady ? (
          <Button type="button" variant="ghost" size="sm" onClick={onView}>
            View
            <span className="sr-only"> {d.name}</span>
          </Button>
        ) : null}
      </div>
    </li>
  );
}
